// Data access layer for event rows. The whole frontend reads events through here.
//
// Source of truth is the static JSON bundle built by
// `scripts/build-events-json.ts` and served from `/data/events.json`.
// Each event keeps the nested shape from `data/events/*.json`
// (counts.*, location.*, sources[], i18n maps).

import { dayIndexFromDate } from './url-state';
import { config } from './config';
import { getPrimaryCount } from './counts';

// ---------------------------------------------------------------------------
// i18n primitives
// ---------------------------------------------------------------------------

/**
 * JSONB i18n map: { en: "…", es: "…", ar: "…", … }. The key is an ISO-639-1
 * language code; the value is the translated text. Missing languages fall
 * back to English (or the original text column when the map is empty).
 */
export type I18nMap = Record<string, string>;

/**
 * Resolve a translated string from an i18n map with a graceful fallback
 * chain: (requested lang) → en → original-text fallback.
 */
export function pickI18n(
  i18n: I18nMap | null | undefined,
  fallback: string | null | undefined,
  lang: string,
): string {
  if (i18n && typeof i18n === 'object') {
    const localized = i18n[lang];
    if (typeof localized === 'string' && localized.length > 0) return localized;
    const english = i18n.en;
    if (typeof english === 'string' && english.length > 0) return english;
  }
  return fallback ?? '';
}

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/**
 * The primary category of an event. Apps narrow this via
 * `app.config.ts.taxonomy.categories[*].id`.
 */
export type Category = string;

export type LocationPrecision = 'exact' | 'approximate' | 'estimated';

/**
 * Optional secondary classification of an event (e.g. a subtype or
 * variant). Apps narrow this via `app.config.ts.taxonomy.secondaryValues`.
 */
export type Secondary = string;

/** Generic life-cycle states; meaning is up to the app. */
export type EventStatus = 'active' | 'concluded' | 'historical';

export type Confidence = 'verified' | 'preliminary' | 'unverified';
export type IngestionMethod = 'manual' | 'auto' | 'auto-reviewed';

// ---------------------------------------------------------------------------
// Event record (nested shape — matches data/events/*.json)
// ---------------------------------------------------------------------------

export interface EventSource {
  url: string;
  title: string;
  title_i18n?: I18nMap;
  publisher: string;
  publisher_i18n?: I18nMap;
  tier: 1 | 2 | 3 | 4;
  accessed: string;
  quote?: string;
}

export interface EventCounts {
  confirmed: number;
  suspected: number;
  deaths: number;
  last_updated: string;
}

export interface EventLocation {
  name: string;
  name_i18n?: I18nMap;
  lat: number;
  lng: number;
  precision?: LocationPrecision;
}

export interface EventIngestion {
  method: IngestionMethod;
  last_updated: string;
}

/**
 * Optional story-arc entry for an event: a single state change such
 * as opening, a count update, or closure. Present in the JSON
 * bundle only when an event has logged history. Rendered as a timeline
 * beneath the summary on the detail panel.
 */
export type EventHistoryType =
  | 'opened'
  | 'updated'
  | 'closed'
  | 'note';

export interface EventHistoryEntry {
  event_at: string;
  event_type: EventHistoryType;
  headline: string;
  headline_i18n?: I18nMap;
  details?: string | null;
  details_i18n?: I18nMap;
  delta?: Record<string, unknown>;
  source_url?: string | null;
  source_publisher?: string | null;
  source_tier?: 1 | 2 | 3 | 4 | null;
}

export interface EventRecord {
  id: string;
  category: Category;
  secondary: Secondary;
  country_iso2: string;
  countries_affected: string[];
  region: string | null;
  region_i18n?: I18nMap;
  location: EventLocation;
  start_date: string;
  end_date: string | null;
  counts: EventCounts;
  summary: string;
  summary_i18n?: I18nMap;
  significance: string | null;
  significance_i18n?: I18nMap;
  sources: EventSource[];
  status: EventStatus;
  confidence: Confidence;
  ingestion: EventIngestion;
  updated_at?: string;
  events?: EventHistoryEntry[];
}

// Back-compat alias for older callers.
export type SourceRow = EventSource;

// ---------------------------------------------------------------------------
// Fetch + cache
// ---------------------------------------------------------------------------

interface BundleShape {
  events: EventRecord[];
}

let cache: EventRecord[] | null = null;
let inflight: Promise<EventRecord[]> | null = null;

/**
 * Load the minimal events payload from `/api/markers` (Cloudflare Pages
 * Function, gated by origin + per-IP rate limit). The full record for a
 * single event — summary, significance, sources, vaccine_response — is
 * fetched lazily via `fetchEventDetail(id)`.
 *
 * The legacy `/data/events.json` static bundle is gone; that path now
 * returns 410 Gone via `frontend/functions/data/events.json.ts`.
 */
export async function fetchEvents(): Promise<EventRecord[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch('/api/markers', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`/api/markers -> ${res.status}`);
      const body = (await res.json()) as BundleShape | EventRecord[];
      // Defensive: accept either { events: [...] } or a bare array.
      const rows = Array.isArray(body) ? body : (body.events ?? []);
      // Sort newest-first.
      rows.sort((a, b) => b.start_date.localeCompare(a.start_date));
      cache = rows;
      return rows;
    } catch (err) {
      console.error('[events] fetch failed', err);
      throw err;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/**
 * The "headline" count for an event — used for marker sizing, list
 * highlights, histogram bars, and any other place that needs a single
 * number per event. Returns the value of whichever counts field is marked
 * `primary: true` in `app.config.ts`. Defensive: missing fields count as 0.
 */
export function totalCases(o: EventRecord): number {
  return getPrimaryCount(o.counts as unknown as Record<string, number | undefined>, config);
}

export function isActive(o: EventRecord): boolean {
  return o.status === 'active';
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------
//
// Geographic + temporal filters applied client-side. The dataset is small
// (<1k rows for the foreseeable future), so streaming + filtering in-browser
// keeps the UX snappy.
//
// IMPORTANT — filter semantics:
// The dayRange filter is STRICT: we include an event iff its start_date
// falls within the window. We deliberately do NOT include long-running
// events that merely OVERLAP the window. That overlap-based version
// silently inflates stats — a "last 7 days" view would sum the cumulative
// count of an event that started 10 months ago, which is false.
//
// Strict-by-start makes the timeline filter mean "events newly reported in
// this window." Active events that started before the window stay
// reachable via the clickable 'X active' badge in the sidebar header,
// which is time-independent.

export interface FilterState {
  dayRange: [number, number];
  categories: Set<string>;
  secondaries: Set<string>;
  statuses: Set<string>;
  confidences: Set<string>;
}

export function applyFilters(rows: EventRecord[], f: FilterState): EventRecord[] {
  const [from, to] = f.dayRange;
  return rows.filter(o => {
    const startIdx = dayIndexFromDate(o.start_date);
    if (startIdx < from || startIdx > to) return false;
    if (f.categories.size && !f.categories.has(o.category)) return false;
    if (f.secondaries.size && !f.secondaries.has(o.secondary)) return false;
    if (f.statuses.size && !f.statuses.has(o.status)) return false;
    if (f.confidences.size && !f.confidences.has(o.confidence)) return false;
    return true;
  });
}

/**
 * All-time aggregate stats for a slice of events. Used in the header strip.
 *
 * Returns:
 *   - events:    row count
 *   - counts:    sum of the primary counts field (matches `totalCases`)
 *   - countries: distinct ISO-2 codes touched
 *   - byField:   per-field sums keyed by `config.counts.fields[i].id`
 */
export function aggregateStats(rows: EventRecord[]): {
  events: number;
  counts: number;
  countries: number;
  byField: Record<string, number>;
} {
  const byField: Record<string, number> = {};
  for (const f of config.counts.fields) byField[f.id] = 0;
  const countries = new Set<string>();
  for (const o of rows) {
    const c = o.counts as unknown as Record<string, number | undefined>;
    for (const f of config.counts.fields) {
      byField[f.id] += c?.[f.id] ?? 0;
    }
    for (const cc of o.countries_affected) countries.add(cc);
  }
  const primary = config.counts.fields.find((f) => f.primary);
  const primaryTotal = primary ? byField[primary.id] : 0;
  return {
    events: rows.length,
    counts: primaryTotal,
    countries: countries.size,
    byField,
  };
}
